import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Maximize2, Minimize2 } from 'lucide-react';

/**
 * Fullscreen toggle rendered into Leaflet's own top-right corner, below
 * the layers widget.
 *
 * It portals into `.leaflet-top.leaflet-right` rather than rendering a
 * div with those class names: that corner is a positioned container, so
 * a second element carrying the same classes stacks *on top of* the
 * layers control instead of below it.
 */
export function MapFullscreenControl({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['common']);
  const map = useMap();
  const [corner, setCorner] = useState<HTMLElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = map
      .getContainer()
      .querySelector<HTMLElement>('.leaflet-top.leaflet-right');
    setCorner(el);
  }, [map]);

  // Keep clicks and scrolls on the button from reaching the map.
  useEffect(() => {
    const el = controlRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, [corner]);

  if (!corner) return null;

  const label = active ? t('common:map.exitFullscreen') : t('common:map.fullscreen');
  const Icon = active ? Minimize2 : Maximize2;

  return createPortal(
    <div ref={controlRef} className="leaflet-control leaflet-bar !border-0 !bg-transparent">
      <button
        type="button"
        onClick={onToggle}
        title={label}
        aria-label={label}
        className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white/95 text-slate-700 shadow-md backdrop-blur-sm transition-colors hover:bg-slate-50 hover:text-forest-700 dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        <Icon className="h-4 w-4" />
      </button>
    </div>,
    corner,
  );
}
